import React, { useState } from 'react';
import clsx from 'clsx';
import type { AddressFieldValue, AddressSubValue } from '@checkout-page/schemas';
import { Checkbox } from '../Form';
import Label from '../CheckoutForm/components/Label';
import StyledElement from '../StyledElement';
import { useCheckoutLocalization } from '../../hooks/useCheckoutLocalization';
import { translateAddressKey } from '../../lib/addressTranslations';
import { AddressInputs } from './AddressInputs';

type AddressFieldConfig = {
  _id: string;
  label: string;
  required?: boolean;
  collect: 'billing' | 'billing_and_shipping';
  collectShippingPhone?: boolean;
  limitShippingCountries?: { enabled?: boolean; countries?: string[] };
  limitBillingCountries?: { enabled?: boolean; countries?: string[] };
  defaultCountry?: string;
  /** Replaces "Billing address" over a single address, e.g. for a guest's own address. */
  title?: string;
};

type Props = {
  field: AddressFieldConfig;
  value: AddressFieldValue | undefined;
  onChange: (value: AddressFieldValue) => void;
  errors?: {
    billing?: Partial<Record<string, string>>;
    shipping?: Partial<Record<string, string>>;
  };
};

function emptyAddress(country: string): AddressSubValue {
  return { country };
}

function singleAllowedCountry(
  limit: { enabled?: boolean; countries?: string[] } | undefined,
): string | null {
  if (!limit?.enabled) return null;
  if (!Array.isArray(limit.countries) || limit.countries.length !== 1) return null;
  return limit.countries[0];
}

export function AddressField({ field, value, onChange, errors }: Props) {
  const isDual = field.collect === 'billing_and_shipping';
  const required = field.required ?? true;

  const { locale } = useCheckoutLocalization();
  const t = (key: 'ui.billingAddress' | 'ui.shippingAddress' | 'ui.billingSameAsShipping') =>
    translateAddressKey(locale, key);

  // When the seller has restricted a side to a single country, that wins over
  // the field-level default country so the address rule (and format) matches
  // the only country the customer can pick.
  const billingDefaultCountry =
    singleAllowedCountry(field.limitBillingCountries) ?? field.defaultCountry ?? 'US';
  const shippingDefaultCountry =
    singleAllowedCountry(field.limitShippingCountries) ?? field.defaultCountry ?? 'US';

  const billing = value?.billing ?? emptyAddress(billingDefaultCountry);
  const shipping = value?.shipping ?? emptyAddress(shippingDefaultCountry);
  const [sameAsShipping, setSameAsShipping] = useState<boolean>(value?.sameAsShipping ?? true);

  const emit = (next: Partial<AddressFieldValue>) => {
    if (!isDual) {
      onChange({ billing: next.billing ?? billing });
      return;
    }
    const nextSameAsShipping = next.sameAsShipping ?? sameAsShipping;
    onChange({
      shipping: next.shipping ?? shipping,
      billing: nextSameAsShipping ? undefined : (next.billing ?? billing),
      sameAsShipping: nextSameAsShipping,
    });
  };

  const sameAsShippingId = `${field._id}-same-as-shipping`;
  const sectionLabelClass = 'cp_form-label cp-address-section-label';

  if (!isDual) {
    return (
      <div className="cp-address-field space-y-4">
        <div className="cp-address-section">
          <StyledElement
            as={Label}
            elementPath="checkout.form.fields.label"
            htmlFor={`${field._id}-billing-addr-country`}
            className={sectionLabelClass}
            checkEnabled={false}
            required={required}
          >
            {field.title || t('ui.billingAddress')}
          </StyledElement>
          <AddressInputs
            idPrefix={`${field._id}-billing-addr`}
            value={billing}
            onChange={b => emit({ billing: b })}
            required={required}
            errors={errors?.billing}
            enableAutocomplete
            limitAllowedCountries={field.limitBillingCountries}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="cp-address-field space-y-4">
      <div className="cp-address-section">
        <StyledElement
          as={Label}
          elementPath="checkout.form.fields.label"
          htmlFor={`${field._id}-shipping-addr-name`}
          className={sectionLabelClass}
          checkEnabled={false}
          required={required}
        >
          {t('ui.shippingAddress')}
        </StyledElement>
        <AddressInputs
          idPrefix={`${field._id}-shipping-addr`}
          value={shipping}
          onChange={s => emit({ shipping: s })}
          required={required}
          errors={errors?.shipping}
          enableAutocomplete
          limitAllowedCountries={field.limitShippingCountries}
          showName
          showPhone={field.collectShippingPhone}
        />
      </div>

      <div className="cp-address-same-as-shipping flex items-center">
        <Checkbox
          id={sameAsShippingId}
          name={sameAsShippingId}
          value={sameAsShipping}
          onChange={(e: { target: { value: boolean } }) => {
            const v = e.target.value;
            setSameAsShipping(v);
            emit({ sameAsShipping: v });
          }}
          className="cp_form-checkbox mr-2"
        />
        <StyledElement
          as={Label}
          elementPath="checkout.form.fields.label"
          htmlFor={sameAsShippingId}
          className={clsx('cp_form-label cp_form-checkbox-label')}
          style={{ marginBottom: '0px', paddingBottom: '0px' }}
          checkEnabled={false}
        >
          {t('ui.billingSameAsShipping')}
        </StyledElement>
      </div>

      {!sameAsShipping && (
        <div className="cp-address-section border-t border-[var(--cp_border-subtle)] pt-4">
          <StyledElement
            as={Label}
            elementPath="checkout.form.fields.label"
            htmlFor={`${field._id}-billing-addr-country`}
            className={sectionLabelClass}
            checkEnabled={false}
            required={required}
          >
            {t('ui.billingAddress')}
          </StyledElement>
          <AddressInputs
            idPrefix={`${field._id}-billing-addr`}
            value={billing}
            onChange={b => emit({ billing: b })}
            required={required}
            errors={errors?.billing}
            enableAutocomplete
            limitAllowedCountries={field.limitBillingCountries}
          />
        </div>
      )}
    </div>
  );
}
